import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import { formatNumber } from '../../utils/formatNumber';
import { bookingService } from '../../services/bookingService';
import { reviewService } from '../../services/reviewService';
import Toast from '../../components/ui/Toast';
import Header from '../../components/layout/Header';
import BackButton from '../../components/BackButton';

const BookingsManagement = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [toastNotification, setToastNotification] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bookingService.getMyBookings('customer');
      const formattedBookings = data?.map(booking => ({
        id: booking?.id,
        photographerName: booking?.photographer?.fullName || booking?.photographer?.full_name || 'مصور',
        photographerEmail: booking?.photographer?.email || '',
        photographerPhone: booking?.photographer?.phone || '',
        photographerImage: booking?.photographer?.avatarUrl || booking?.photographer?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1bbd80f16-1763294584961.png',
        serviceType: booking?.serviceType || booking?.service_type,
        photographyType: booking?.photographyType || booking?.photography_type,
        date: booking?.bookingDate || booking?.booking_date,
        time: booking?.bookingTime || booking?.booking_time,
        location: booking?.location,
        city: booking?.city,
        price: parseFloat(booking?.price || 0),
        status: booking?.status,
        duration: booking?.duration,
        equipment: booking?.equipment || [],
        notes: booking?.notes,
        createdAt: booking?.createdAt || booking?.created_at,
        updatedAt: booking?.updatedAt || booking?.updated_at
      }));
      setBookings(formattedBookings);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError(err?.message || 'فشل تحميل الحجوزات');
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.photographerName?.toLowerCase().includes(query) ||
        booking.serviceType?.toLowerCase().includes(query) ||
        booking.photographyType?.toLowerCase().includes(query) ||
        booking.location?.toLowerCase().includes(query) ||
        booking.city?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.date);
        switch (dateFilter) {
          case 'today':
            return bookingDate.toDateString() === now.toDateString();
          case 'thisWeek':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return bookingDate >= weekAgo;
          case 'thisMonth':
            return bookingDate.getMonth() === now.getMonth() && bookingDate.getFullYear() === now.getFullYear();
          case 'upcoming':
            return bookingDate > now;
          case 'past':
            return bookingDate < now;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  }, [bookings, searchQuery, statusFilter, dateFilter]);

  const handleViewBooking = (bookingId) => {
    navigate(`/booking-confirmation?id=${bookingId}`);
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;
    
    try {
      await bookingService.updateBookingStatus(bookingId, 'cancelled');
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b));
      setToastNotification({
        title: 'تم الإلغاء',
        message: 'تم إلغاء الحجز بنجاح',
        icon: 'CheckCircle',
        iconColor: 'var(--color-success)'
      });
      loadBookings();
    } catch (err) {
      setToastNotification({
        title: 'خطأ',
        message: err?.message || 'فشل إلغاء الحجز',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)'
      });
    }
  };

  const handleRateBooking = (bookingId) => {
    navigate(`/booking-confirmation?id=${bookingId}&rate=true`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: 'مؤكد', color: 'var(--color-success)', bgColor: 'rgba(16, 185, 129, 0.1)' },
      pending: { label: 'قيد الانتظار', color: 'var(--color-warning)', bgColor: 'rgba(245, 158, 11, 0.1)' },
      completed: { label: 'مكتمل', color: 'var(--color-primary)', bgColor: 'rgba(37, 99, 235, 0.1)' },
      cancelled: { label: 'ملغي', color: 'var(--color-error)', bgColor: 'rgba(239, 68, 68, 0.1)' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span
        className="px-3 py-1 rounded-full text-sm font-medium"
        style={{ color: config?.color, backgroundColor: config?.bgColor }}
      >
        {config?.label}
      </span>
    );
  };

  const stats = useMemo(() => {
    const total = bookings.length;
    const confirmed = bookings.filter(b => b.status === 'confirmed').length;
    const pending = bookings.filter(b => b.status === 'pending').length;
    const completed = bookings.filter(b => b.status === 'completed').length;
    const cancelled = bookings.filter(b => b.status === 'cancelled').length;

    return { total, confirmed, pending, completed, cancelled };
  }, [bookings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل الحجوزات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50">
      <Header />
      <main className="pt-16 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="mb-4">
            <BackButton to="/customer-dashboard" label="الرئيسية" />
          </div>
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              إدارة الحجوزات
            </h1>
            <p className="text-slate-600 text-lg">
              عرض وإدارة جميع حجوزاتك في مكان واحد
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-600">إجمالي الحجوزات</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
              <div className="text-sm text-slate-600">مؤكدة</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <div className="text-sm text-slate-600">قيد الانتظار</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
              <div className="text-sm text-slate-600">مكتملة</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
              <div className="text-sm text-slate-600">ملغاة</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن حجز..."
                  className="w-full pr-10 pl-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="all">جميع الحالات</option>
                <option value="pending">قيد الانتظار</option>
                <option value="confirmed">مؤكد</option>
                <option value="completed">مكتمل</option>
                <option value="cancelled">ملغي</option>
              </select>

              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="all">جميع التواريخ</option>
                <option value="today">اليوم</option>
                <option value="thisWeek">هذا الأسبوع</option>
                <option value="thisMonth">هذا الشهر</option>
                <option value="upcoming">قادمة</option>
                <option value="past">سابقة</option>
              </select>
            </div>
          </div>

          {/* Bookings List */}
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadBookings}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
              <Icon name="Inbox" size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">لا توجد حجوزات</h3>
              <p className="text-slate-600 mb-6">
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all'
                  ? 'لا توجد نتائج تطابق معايير البحث'
                  : 'لم تقم بأي حجوزات بعد'}
              </p>
              {!searchQuery && statusFilter === 'all' && dateFilter === 'all' && (
                <button
                  onClick={() => navigate('/booking-wizard')}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  احجز الآن
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Photographer Image */}
                      <img
                        src={booking.photographerImage}
                        alt={booking.photographerName}
                        className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                      />

                      {/* Booking Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-1">
                              {booking.photographerName}
                            </h3>
                            <p className="text-sm text-slate-600 mb-2">
                              {booking.photographyType} - {booking.serviceType}
                            </p>
                          </div>
                          {getStatusBadge(booking.status)}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Icon name="Calendar" size={16} />
                            <span>{new Date(booking.date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Icon name="Clock" size={16} />
                            <span>{booking.time}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Icon name="MapPin" size={16} />
                            <span>{booking.city}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <Icon name="DollarSign" size={16} />
                            <span>{formatNumber(booking.price)} ريال</span>
                          </div>
                        </div>

                        {booking.location && (
                          <div className="mb-4">
                            <p className="text-sm text-slate-600">
                              <span className="font-semibold">الموقع:</span> {booking.location}
                            </p>
                          </div>
                        )}

                        {booking.notes && (
                          <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-700">
                              <span className="font-semibold">ملاحظات:</span> {booking.notes}
                            </p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleViewBooking(booking.id)}
                            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                          >
                            عرض التفاصيل
                          </button>
                          {booking.status === 'completed' && (
                            <button
                              onClick={() => handleRateBooking(booking.id)}
                              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
                            >
                              تقييم الخدمة
                            </button>
                          )}
                          {booking.status === 'pending' && (
                            <button
                              onClick={() => handleCancelBooking(booking.id)}
                              className="px-4 py-2 bg-error/10 text-error rounded-lg text-sm font-medium hover:bg-error/20 transition-colors"
                            >
                              إلغاء الحجز
                            </button>
                          )}
                          {booking.photographerPhone && (
                            <a
                              href={`tel:${booking.photographerPhone}`}
                              className="px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors flex items-center gap-2"
                            >
                              <Icon name="Phone" size={16} />
                              <span>اتصل</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Toast Notification */}
      {toastNotification && (
        <Toast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default BookingsManagement;
